import { ReactNode } from 'react';

import { CountryEntity, CountryRiskLevelType } from 'api/types/entity';
import { useTranslation } from 'libs/i18n';
import { Tooltip } from 'libs/ui';
import { Icon } from 'libs/ui/Icon';
import { Colors } from 'libs/ui/theme';
import { Maybe } from 'types/maybe';

import { CountryRiskBadgesStyled } from './CountryRiskBadges.styles';
import { countryAlertTypes } from './countryAlertTypes';
import { CountryAlertType, CountryRiskField, CountryRiskRole } from './types';
import { useCountryRiskContext } from './useCountryRiskContext';

export interface CountryRiskBadgesProps {
  country?: Maybe<CountryEntity>;
  field: CountryRiskField;
  /** Role of the field owner when it differs from the provider role */
  role?: CountryRiskRole;
}

const riskLevelIcons: Record<CountryRiskLevelType, ReactNode> = {
  [CountryRiskLevelType.Prohibited]: <Icon.Cross color={Colors.red500} />,
  [CountryRiskLevelType.High]: <Icon.Flag color={Colors.red500} />,
  [CountryRiskLevelType.Medium]: <Icon.Flag color={Colors.yellow500} />,
  [CountryRiskLevelType.Low]: <Icon.Flag color={Colors.green500} />,
};

const alertTypeIcons: Record<CountryAlertType, ReactNode> = {
  [CountryAlertType.HardStop]: <Icon.AlertTriangle color={Colors.red500} />,
  [CountryAlertType.ComplianceReview]: (
    <Icon.AlertCircle color={Colors.yellow500} glyphColor={Colors.black} />
  ),
};

export const CountryRiskBadges = ({
  country,
  field,
  role,
}: CountryRiskBadgesProps) => {
  const { t } = useTranslation();
  const context = useCountryRiskContext();

  const countryRiskLevels = context?.countryRiskLevels;
  const riskRole = role ?? context?.role;
  const alertType = riskRole && countryAlertTypes[riskRole][field];

  if (!country?.id || !countryRiskLevels || !alertType) {
    return null;
  }

  if (!countryRiskLevels.has(country.id)) {
    return (
      <Tooltip title={t('client.countryRisk.notFound')}>
        <CountryRiskBadgesStyled>
          <Icon.QuestionCircle color={Colors.gray600} />
        </CountryRiskBadgesStyled>
      </Tooltip>
    );
  }

  const riskLevel = countryRiskLevels.get(country.id);

  if (!riskLevel) {
    return null;
  }

  const isProhibited = riskLevel === CountryRiskLevelType.Prohibited;
  const riskLevelLabel = t(`client.countryRisk.level.${riskLevel}`);
  const alertTypeLabel = t(`client.countryRisk.alertType.${alertType}`);

  return (
    <Tooltip
      title={
        isProhibited ? `${riskLevelLabel} · ${alertTypeLabel}` : riskLevelLabel
      }
    >
      <CountryRiskBadgesStyled>
        {riskLevelIcons[riskLevel]}
        {isProhibited && alertTypeIcons[alertType]}
      </CountryRiskBadgesStyled>
    </Tooltip>
  );
};
